import { useState, useEffect } from 'react';
import {
  ShieldCheck, Warning, CheckCircle, Info, ArrowClockwise, Download,
  Lightbulb, X, FileText, Globe, Package, Calendar, Bell,
} from '@phosphor-icons/react';
import {
  complianceScan, complianceRequestFix, complianceTemplate,
  complianceCheckNow, complianceHistory,
} from '../services/api';

const RISK_BADGE = {
  none:   { label: 'Sicher',  cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  low:    { label: 'Niedrig', cls: 'bg-sky-100 text-sky-800 border-sky-200' },
  medium: { label: 'Mittel',  cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  high:   { label: 'Hoch',    cls: 'bg-red-100 text-red-800 border-red-200' },
};

const CATEGORY_EXPLAINER = {
  'Safe': 'Permissive Lizenz (MIT, BSD, Apache, ISC, …). Erlaubt kommerzielle Nutzung ohne Auflagen, außer ggf. Copyright-Hinweis.',
  'Dual (GPL/MIT)': 'Paket ist unter mehreren Lizenzen gleichzeitig verfügbar. Du darfst die kommerziell freundliche Variante (z. B. MIT) wählen.',
  'Dual (LGPL/Safe)': 'Paket ist Dual-License — wir wählen die permissive Variante.',
  'Dual (MPL/Safe)': 'Paket ist Dual-License — wir wählen die permissive Variante.',
  'MPL-2.0': 'Weak Copyleft: Nur unmittelbar geänderte MPL-Dateien müssen offen bleiben. Dein eigener Code bleibt geschlossen. Für SaaS und On-Premise OK.',
  'LGPL': 'LGPL: Bei dynamischem Linking (Standard bei Bibliotheken) für SaaS und On-Premise unkritisch.',
  'GPL': 'GPL: Bei SaaS (du verteilst keine Binaries) UNKRITISCH. Bei On-Premise/Download müsstest du eigenen Quellcode offenlegen.',
  'AGPL': 'AGPL: Erzwingt Quellcode-Offenlegung auch bei reiner SaaS-Nutzung. NICHT verwenden.',
  'SSPL': 'SSPL ist nicht OSI-konform und schränkt SaaS-Anbieter explizit ein.',
  'BUSL': 'Business Source License: Untersagt kommerzielle Konkurrenznutzung.',
  'Non-Commercial': 'Kommerziell eingeschränkt (z. B. CC-BY-NC, Elastic License). NICHT verwenden.',
  'Unbekannt': 'Lizenz konnte nicht automatisch klassifiziert werden — bitte manuell prüfen.',
};

const MODE_INFO = {
  saas: {
    title: 'SaaS / Monats-Abo',
    desc: 'Du betreibst die Software selbst auf deinem IONOS-Server. Kunden bekommen nur einen Webzugang, keinen Quellcode. Du verkaufst monatliche Nutzungslizenzen.',
    detail: 'In diesem Modus sind GPL- und LGPL-Pakete unkritisch, da du keine Binaries verteilst (Distribution-Trigger). Nur AGPL, SSPL, BUSL und CC-BY-NC sind problematisch.',
  },
  onprem: {
    title: 'On-Premise / Download-Vertrieb',
    desc: 'Du verkaufst die Software als Installations-Paket — der Kunde lädt sie herunter und installiert sie auf seinem eigenen Server.',
    detail: 'Hier zählt jede Code-Verteilung als „Distribution". GPL-Pakete erzwingen die Offenlegung deines gesamten Quellcodes. Du brauchst nur permissive Lizenzen (MIT/BSD/Apache).',
  },
};

function InfoButton({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-block align-middle">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-700"
        data-testid={`info-btn-${title?.toString().slice(0, 20)}`}
      >
        <Info weight="bold" className="w-3.5 h-3.5" />
      </button>
      {open && (
        <span className="absolute left-1/2 -translate-x-1/2 top-7 z-50 w-72 p-3 rounded-lg bg-slate-900 text-white text-xs shadow-xl normal-case font-normal text-left">
          {title && <span className="block font-semibold mb-1">{title}</span>}
          <span className="block leading-relaxed">{children}</span>
        </span>
      )}
    </span>
  );
}

function PkgTable({ pkgs, title, testid }) {
  if (!pkgs?.length) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 font-semibold text-slate-700">{title} <span className="text-slate-400 font-normal">({pkgs.length})</span></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2">Paket</th>
              <th className="px-4 py-2">Version</th>
              <th className="px-4 py-2">Lizenz</th>
              <th className="px-4 py-2">Kategorie</th>
              <th className="px-4 py-2">Risiko</th>
            </tr>
          </thead>
          <tbody>
            {pkgs.map((p, i) => {
              const risk = p.risk_saas || p.risk_onprem || 'none';
              const b = RISK_BADGE[risk] || RISK_BADGE.none;
              return (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-900 whitespace-nowrap">{p.Name}</td>
                  <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{p.Version}</td>
                  <td className="px-4 py-2 text-slate-600">{p.License}</td>
                  <td className="px-4 py-2 text-slate-700">
                    <div className="inline-flex items-center gap-1">
                      <span>{p.category}</span>
                      <InfoButton title={p.category}>{CATEGORY_EXPLAINER[p.category] || p.explanation}</InfoButton>
                    </div>
                  </td>
                  <td className="px-4 py-2"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${b.cls}`}>{b.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FixRequestModal({ open, onClose, pkg, mode }) {
  const [alt, setAlt] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => { if (open) { setAlt(''); setNote(''); setDone(null); } }, [open]);
  if (!open) return null;
  const submit = async () => {
    setSending(true);
    try {
      const res = await complianceRequestFix({
        package_name: pkg?.Name || 'allgemein',
        current_license: pkg?.License || '',
        current_version: pkg?.Version || '',
        suggested_alternative: alt,
        user_note: note,
        mode,
      });
      setDone({ ok: true, msg: res.data.message || 'Vorschlag gesendet' });
    } catch (e) {
      setDone({ ok: false, msg: e.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="fix-modal">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 space-y-4">
        <div className="flex items-start justify-between">
          <h3 className="font-heading text-xl font-black text-slate-900">Änderung vorschlagen</h3>
          <button onClick={onClose} data-testid="fix-modal-close"><X weight="bold" className="w-5 h-5 text-slate-400" /></button>
        </div>
        {pkg ? (
          <div className="text-sm text-slate-600 bg-slate-50 rounded p-3">
            <div><strong>Paket:</strong> {pkg.Name} {pkg.Version}</div>
            <div><strong>Aktuelle Lizenz:</strong> {pkg.License}</div>
            <div><strong>Kategorie:</strong> {pkg.category}</div>
          </div>
        ) : (
          <div className="text-sm text-slate-600">Allgemeiner Compliance-Vorschlag</div>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Vorgeschlagene Alternative</label>
          <input value={alt} onChange={e => setAlt(e.target.value)} placeholder="z. B. pyheif statt pillow_heif" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="fix-alt-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Notiz / Begründung</label>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={4} placeholder="Warum soll dieses Paket ersetzt werden?" className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="fix-note-input" />
        </div>
        {done && (
          <div className={`text-sm rounded-lg p-3 ${done.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{done.msg}</div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border hover:bg-slate-50">Abbrechen</button>
          <button onClick={submit} disabled={sending} className="px-4 py-2 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50" data-testid="fix-submit-btn">
            {sending ? 'Sende…' : 'Vorschlag senden'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Compliance() {
  const [mode, setMode] = useState('saas');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [fixModal, setFixModal] = useState({ open: false, pkg: null });
  const [tplLoading, setTplLoading] = useState('');
  const [history, setHistory] = useState([]);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const loadHistory = async () => {
    try {
      const res = await complianceHistory(10);
      setHistory(res.data.items || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const runCheckNow = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const res = await complianceCheckNow();
      setCheckResult(res.data);
      await loadHistory();
    } catch (e) {
      setCheckResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setChecking(false);
    }
  };

  const nextMondayLabel = () => {
    const now = new Date();
    const d = new Date(now);
    const day = d.getDay();
    const add = ((1 + 7 - day) % 7) || 7;
    d.setDate(d.getDate() + add);
    d.setHours(7, 0, 0, 0);
    return d.toLocaleString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const load = async (m = mode, force = false) => {
    setError('');
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const res = await complianceScan(m, force);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(mode, false); /* eslint-disable-line */ }, [mode]);

  const downloadTpl = async (name) => {
    setTplLoading(name);
    try {
      const res = await complianceTemplate(name);
      const blob = new Blob([res.data.text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = res.data.filename || `${name}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setTplLoading('');
    }
  };

  const overall = data?.overall_score ?? 0;
  const freigegeben = data?.freigegeben;
  const verdict = freigegeben
    ? { color: 'emerald', icon: CheckCircle, text: `Freigegeben für ${MODE_INFO[mode].title}` }
    : { color: 'red',     icon: Warning,     text: 'Nicht freigegeben — kritische Lizenz erkannt' };

  const allBackend = [...(data?.backend?.high || []), ...(data?.backend?.medium || []), ...(data?.backend?.low || [])];
  const allFrontend = [...(data?.frontend?.high || []), ...(data?.frontend?.medium || []), ...(data?.frontend?.low || [])];

  return (
    <div className="space-y-6 max-w-screen-2xl" data-testid="compliance-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-slate-900 flex items-center gap-2">
            <ShieldCheck weight="bold" className="w-8 h-8 text-emerald-600" />
            Lizenz-Compliance
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Prüft alle Backend- und Frontend-Pakete auf Open-Source-Lizenzen und bewertet das Risiko für deinen Vertriebs-Modus.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(mode, true)}
            disabled={refreshing}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="rescan-btn"
          >
            <ArrowClockwise weight="bold" className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Scanne…' : 'Neu scannen'}
          </button>
          <button
            onClick={() => setFixModal({ open: true, pkg: null })}
            className="px-4 py-2 border-2 border-slate-900 text-slate-900 rounded-lg hover:bg-slate-50 inline-flex items-center gap-2"
            data-testid="general-suggest-btn"
          >
            <Lightbulb weight="bold" className="w-4 h-4" /> Änderung vorschlagen
          </button>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-sm font-semibold text-slate-700">Vertriebs-Modus:</span>
          <InfoButton title="SaaS vs. On-Premise">
            SaaS = du betreibst es selbst (keine Code-Weitergabe). On-Premise = Kunde bekommt eine installierbare Kopie. Die Bewertung der GPL/LGPL-Pakete ist davon abhängig.
          </InfoButton>
        </div>
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
          <button onClick={() => setMode('saas')} className={`px-4 py-2 text-sm font-medium ${mode === 'saas' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`} data-testid="mode-saas">
            SaaS / Monats-Abo
          </button>
          <button onClick={() => setMode('onprem')} className={`px-4 py-2 text-sm font-medium border-l border-slate-300 ${mode === 'onprem' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`} data-testid="mode-onprem">
            On-Premise / Download
          </button>
        </div>
        <p className="text-sm text-slate-600 mt-3 leading-relaxed">
          <strong>{MODE_INFO[mode].title}:</strong> {MODE_INFO[mode].desc}<br />
          <span className="text-slate-500">{MODE_INFO[mode].detail}</span>
        </p>
      </div>

      {/* Error / Loading */}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>
      ) : data && (
        <>
          {/* Verdict cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`bg-white rounded-xl border-2 border-${verdict.color}-200 p-5`} data-testid="verdict-card">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <span>Gesamt-Bewertung</span>
                <InfoButton title="Gesamt-Bewertung">Mittel aus Backend- und Frontend-Score. 100% = nur permissive Lizenzen. Score sinkt bei Copyleft- und Non-Commercial-Lizenzen.</InfoButton>
              </div>
              <div className={`text-3xl font-black text-${verdict.color}-600`}>{overall}%</div>
              <div className={`mt-2 text-sm font-medium text-${verdict.color}-700 flex items-center gap-1`}>
                <verdict.icon weight="bold" className="w-5 h-5" /> {verdict.text}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <span>Backend (Python)</span>
                <InfoButton title="Backend">{data?.backend?.total_packages} installierte Python-Pakete (FastAPI, MongoDB-Treiber, OCR, KI-SDKs …) aus requirements.txt.</InfoButton>
              </div>
              <div className="text-2xl font-bold text-slate-900">{data?.backend?.score}%</div>
              <div className="mt-2 flex gap-1 flex-wrap">
                <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">{data?.backend?.high?.length || 0} Hoch</span>
                <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">{data?.backend?.medium?.length || 0} Mittel</span>
                <span className="px-2 py-0.5 rounded-full text-xs bg-sky-100 text-sky-700">{data?.backend?.low?.length || 0} Niedrig</span>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <span>Frontend (React/NPM)</span>
                <InfoButton title="Frontend">{data?.frontend?.total_packages} installierte NPM-Pakete (React, Tailwind, Axios, Icons …) — inkl. transitiver Abhängigkeiten.</InfoButton>
              </div>
              <div className="text-2xl font-bold text-slate-900">{data?.frontend?.score}%</div>
              <div className="mt-2 flex gap-1 flex-wrap">
                <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">{data?.frontend?.high?.length || 0} Hoch</span>
                <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">{data?.frontend?.medium?.length || 0} Mittel</span>
                <span className="px-2 py-0.5 rounded-full text-xs bg-sky-100 text-sky-700">{data?.frontend?.low?.length || 0} Niedrig</span>
              </div>
            </div>
          </div>

          {/* Ratings table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 font-semibold text-slate-700 flex items-center gap-2">
              Verkaufs-Optionen
              <InfoButton title="Verkaufs-Optionen">Basierend auf den erkannten Lizenzen — kann deine Software in dem jeweiligen Modus rechtssicher verkauft werden?</InfoButton>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-200" data-testid="verkauf-grid">
              <div className="p-4">
                <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  SaaS-Verkauf
                  <InfoButton title="SaaS">Du hostest die Software auf deinem Server. Kunden zahlen monatliche Nutzungsgebühr. GPL ist hier OK.</InfoButton>
                </div>
                <div className={`mt-2 text-lg font-bold ${data?.ratings?.verkauf_saas === 'Ja' ? 'text-emerald-600' : 'text-red-600'}`}>{data?.ratings?.verkauf_saas}</div>
              </div>
              <div className="p-4">
                <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  Download-Verkauf
                  <InfoButton title="Download">Kunde lädt die Software herunter und installiert sie selbst. Hier sind GPL-Pakete problematisch.</InfoButton>
                </div>
                <div className={`mt-2 text-lg font-bold ${data?.ratings?.verkauf_download === 'Ja' ? 'text-emerald-600' : 'text-red-600'}`}>{data?.ratings?.verkauf_download}</div>
              </div>
              <div className="p-4">
                <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  Closed-Source-Verkauf
                  <InfoButton title="Closed-Source">Verkauf ohne Quellcode-Offenlegung. Erfordert permissive Lizenzen für ALLE Abhängigkeiten.</InfoButton>
                </div>
                <div className={`mt-2 text-lg font-bold ${data?.ratings?.verkauf_closed_source === 'Ja' ? 'text-emerald-600' : 'text-red-600'}`}>{data?.ratings?.verkauf_closed_source}</div>
              </div>
            </div>
          </div>

          {/* Templates */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <FileText weight="bold" className="w-5 h-5 text-slate-700" />
              <h3 className="font-heading text-lg font-bold text-slate-900">DSGVO-Templates</h3>
              <InfoButton title="DSGVO-Templates">Vorformulierte AVV- und Datenschutz-Vorlagen für deine SaaS-Kunden — angepasst an deine Mandanten-Daten.</InfoButton>
            </div>
            <p className="text-sm text-slate-500 mb-4">Für die Vermietung als SaaS musst du mit jedem Verwalter einen AVV (Art. 28 DSGVO) abschließen und eine Datenschutzerklärung bereitstellen.</p>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => downloadTpl('avv')}
                disabled={tplLoading === 'avv'}
                className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="avv-download"
              >
                <Download weight="bold" className="w-4 h-4" /> {tplLoading === 'avv' ? 'Lade…' : 'AVV-Vertrag (Art. 28 DSGVO)'}
              </button>
              <button
                onClick={() => downloadTpl('datenschutz')}
                disabled={tplLoading === 'datenschutz'}
                className="px-4 py-2 border-2 border-slate-900 text-slate-900 rounded-lg hover:bg-slate-50 inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="datenschutz-download"
              >
                <Download weight="bold" className="w-4 h-4" /> {tplLoading === 'datenschutz' ? 'Lade…' : 'Datenschutzerklärung'}
              </button>
            </div>
          </div>

          {/* Auto Weekly Check */}
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="autocheck-panel">
            <div className="flex items-center gap-2 mb-3">
              <Bell weight="bold" className="w-5 h-5 text-amber-600" />
              <h3 className="font-heading text-lg font-bold text-slate-900">Automatische Wochen-Prüfung</h3>
              <InfoButton title="Wochen-Prüfung">
                Jeden Montag um 07:00 wird ein automatischer Lizenz-Scan ausgeführt. Du bekommst nur dann eine E-Mail, wenn ein NEUES Hoch-Risiko-Paket dazugekommen ist oder der Score deutlich gefallen ist. So merkst du sofort, wenn ein `yarn upgrade` oder `pip install` ein riskantes Paket einschleust.
              </InfoButton>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-slate-500 text-xs uppercase tracking-wide mb-1">Letzter Lauf</div>
                <div className="font-medium text-slate-900" data-testid="autocheck-last">
                  {history[0]?.ran_at
                    ? new Date(history[0].ran_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : '— noch keine Prüfung —'}
                </div>
                {history[0] && (
                  <div className="text-xs text-slate-500 mt-1">
                    Score: SaaS {history[0].saas_score}% · OnPrem {history[0].onprem_score}%
                  </div>
                )}
              </div>
              <div>
                <div className="text-slate-500 text-xs uppercase tracking-wide mb-1">Nächster Lauf</div>
                <div className="font-medium text-slate-900 flex items-center gap-1" data-testid="autocheck-next">
                  <Calendar weight="bold" className="w-4 h-4 text-slate-400" />
                  {nextMondayLabel()}
                </div>
                <div className="text-xs text-slate-500 mt-1">Cron: Mo 07:00 Europe/Berlin</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs uppercase tracking-wide mb-1">Letzte Risiko-Änderung</div>
                <div className="font-medium" data-testid="autocheck-risk">
                  {history[0]?.new_high?.length ? (
                    <span className="text-red-600">+{history[0].new_high.length} neue Hoch-Risiken</span>
                  ) : history[0]?.score_drop > 0 ? (
                    <span className="text-amber-600">Score -{history[0].score_drop}</span>
                  ) : (
                    <span className="text-emerald-600">Keine Änderungen</span>
                  )}
                </div>
                {history[0]?.new_high?.length ? (
                  <div className="text-xs text-slate-500 mt-1 break-words">{history[0].new_high.join(', ')}</div>
                ) : null}
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <button
                onClick={runCheckNow}
                disabled={checking}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-2 text-sm font-medium"
                data-testid="autocheck-run-btn"
              >
                <Bell weight="bold" className={`w-4 h-4 ${checking ? 'animate-pulse' : ''}`} />
                {checking ? 'Prüfe…' : 'Jetzt prüfen (E-Mail bei Risiko)'}
              </button>
              {checkResult && checkResult.ok && (
                <span className="text-sm text-slate-600">
                  ✓ Geprüft · SaaS {checkResult.saas_score}% · {checkResult.notified ? `E-Mail an ${checkResult.notified}` : 'keine E-Mail (alles stabil)'}
                </span>
              )}
              {checkResult && !checkResult.ok && (
                <span className="text-sm text-red-600">Fehler: {checkResult.error}</span>
              )}
            </div>
            {history.length > 1 && (
              <details className="mt-4">
                <summary className="text-sm text-slate-600 cursor-pointer hover:text-slate-900">Verlauf anzeigen ({history.length} Läufe)</summary>
                <table className="mt-2 w-full text-xs">
                  <thead className="text-slate-500">
                    <tr><th className="text-left py-1">Datum</th><th className="text-left">SaaS</th><th className="text-left">OnPrem</th><th className="text-left">Neue Hoch-Risiken</th></tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="py-1">{new Date(h.ran_at).toLocaleString('de-DE')}</td>
                        <td>{h.saas_score}%</td>
                        <td>{h.onprem_score}%</td>
                        <td className="text-red-600">{h.new_high?.length ? h.new_high.join(', ') : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>

          {/* Packages with issues */}
          {(data?.backend?.high?.length || data?.backend?.medium?.length || data?.backend?.low?.length) ? (
            <div className="space-y-2">
              <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                <Package weight="bold" className="w-5 h-5 text-slate-700" /> Backend — Aufmerksamkeits-Liste
              </h3>
              <PkgTable testid="backend-table" pkgs={allBackend} title="Backend-Pakete mit besonderer Lizenz" />
            </div>
          ) : null}
          {(data?.frontend?.high?.length || data?.frontend?.medium?.length || data?.frontend?.low?.length) ? (
            <div className="space-y-2">
              <h3 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                <Globe weight="bold" className="w-5 h-5 text-slate-700" /> Frontend — Aufmerksamkeits-Liste
              </h3>
              <PkgTable testid="frontend-table" pkgs={allFrontend} title="Frontend-Pakete mit besonderer Lizenz" />
            </div>
          ) : null}

          <p className="text-xs text-slate-400 text-right">Scan: {new Date(data?.scanned_at).toLocaleString('de-DE')}</p>
        </>
      )}

      <FixRequestModal open={fixModal.open} onClose={() => setFixModal({ open: false, pkg: null })} pkg={fixModal.pkg} mode={mode} />
    </div>
  );
}
